import { useState } from 'react';
import { Button, Card, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconCloseButton from '@/components/common/FalconCloseButton';
import { CHANGELOG_DOCS_URL } from '@/config';

type AppUpdateBannerProps = {
  open: boolean;
  onDismiss: () => void;
};

const clearAppCachesAndReload = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map(registration => registration.unregister())
      );
    }

    if ('caches' in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map(key => caches.delete(key)));
    }
  } catch (error) {
    console.error('Failed to clear app caches', error);
  } finally {
    const url = new URL(window.location.href);
    url.searchParams.delete('preview_update');
    url.searchParams.set('_refresh', String(Date.now()));
    window.location.replace(url.toString());
  }
};

const AppUpdateBanner = ({ open, onDismiss }: AppUpdateBannerProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  if (!open) return null;

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    await clearAppCachesAndReload();
  };

  return (
    <div
      className="position-fixed m-3"
      style={{ top: 0, left: 0, zIndex: 1080, maxWidth: 'min(420px, calc(100vw - 1.5rem))' }}
      role="status"
      aria-live="polite"
    >
      <Card className="shadow border">
        <Card.Body className="p-3">
          <div className="d-flex align-items-start gap-2 mb-3">
            <div
              className="d-inline-flex flex-shrink-0 align-items-center justify-content-center rounded-3 bg-primary text-white"
              style={{ width: '2.25rem', height: '2.25rem' }}
              aria-hidden
            >
              <FontAwesomeIcon icon="sync-alt" />
            </div>

            <div className="flex-grow-1 min-w-0 pt-1">
              <div className="fw-bold fs-0 lh-sm">آپدیت جدید آماده است</div>
              <p className="mb-0 mt-1 fs--1 text-600 lh-base">
                برای مشاهده آخرین تغییرات پنل، روی دکمه زیر کلیک کنید.
              </p>
            </div>

            <FalconCloseButton
              noOutline
              className="flex-shrink-0 p-1"
              disabled={isRefreshing}
              onClick={onDismiss}
            />
          </div>

          <div
            className="d-flex align-items-center justify-content-between gap-2"
            style={{ direction: 'ltr' }}
          >
            <Button
              size="sm"
              variant="falcon-primary"
              className="d-inline-flex align-items-center gap-1 border-0"
              disabled={isRefreshing}
              onClick={handleRefresh}
            >
              {isRefreshing ? (
                <>
                  در حال به‌روزرسانی…
                  <Spinner animation="border" size="sm" />
                </>
              ) : (
                <>
                  پاک کردن کش و رفرش
                  <FontAwesomeIcon icon="redo" />
                </>
              )}
            </Button>

            <a
              href={CHANGELOG_DOCS_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="fs--2 fw-semi-bold text-nowrap"
              style={{ direction: 'rtl' }}
            >
              مشاهده تغییرات
            </a>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AppUpdateBanner;
